import { Form, FormControl, FormField, FormGroup, FormItem, FormMessage } from "@/components/core/forms/form";
import {
	caseEvidenceNoteQueryOptions,
	useEvidenceNoteDeleteMutation,
	useEvidenceNoteMutation,
} from "@/features/case-evaluation/api/services/evidence-notes-query";
import { type CaseEvidence, type EvidenceNoteForm, evidenceNoteFormSchema } from "@/features/case-evaluation/types/evidence";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import userService from "@/features/dashboard/api/services/user-service";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconDots, IconFile, IconMoodLookDown, IconPaperclip, IconSend2, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { type ChangeEvent, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "../core/actions/dropdown-menu";
import { Blanket, BlanketImage, BlanketInfo } from "../core/blanket";
import { Button } from "../core/buttons/button";
import { FileLinkWithViewer } from "../core/file-link-with-viewer";
import TagsInput from "../core/forms/tags-input";
import { useModal } from "../core/modals/use-modal";
import { useModalControl } from "../core/modals/use-modal-controller";
import Tooltip from "../core/tooltip";

export default function CaseEvidenceNotesModal({
	caseRecord,
	evidence,
	branchId,
}: {
	caseRecord: CaseRecord;
	evidence: CaseEvidence;
	branchId: string;
}) {
	const notesQuery = useSuspenseQuery(
		caseEvidenceNoteQueryOptions({
			case_evidence_id: evidence.id,
		}),
	);

	const notes = notesQuery.data?.data;
	const { user } = useAuth();
	const { closeModal } = useModalControl();
	const modal = useModal();

	const inputRef = useRef<HTMLInputElement | null>(null);
	const [inputValue, setInputValue] = useState<string>("");

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setInputValue(newValue);
	};

	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const evidenceNoteMutation = useEvidenceNoteMutation(caseRecord.id);
	const caseEvidenceNoteDeleteMutation = useEvidenceNoteDeleteMutation(caseRecord.id);

	const form = useForm<EvidenceNoteForm>({
		resolver: zodResolver(evidenceNoteFormSchema),
		mode: "onChange",
		defaultValues: {
			id: null,
			case_evidence_id: evidence.id,
			case_record_id: caseRecord.id,
			note: "",
			share_with: null,
			attachment: null,
		},
	});

	const handleSubmit = async (values: any) => {
		await evidenceNoteMutation.mutateAsync(values);
		form.setValue("note", "");
		form.setValue("attachment", null);
		form.setValue("share_with", []);
		setMultipleFiles([]);
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const objectUrl = URL.createObjectURL(files[0]);
			setSelectedFiles((prevFiles) => {
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles);
				setMultipleFiles(newFiles);
				return newFiles;
			});
		}
	};

	const handleEditNote = (note: any) => {
		form.setValue("id", note.id);
		form.setValue("note", note.note);
		form.setValue("case_evidence_id", note.case_evidence_id);
		form.setValue("case_record_id", note.case_record_id);
		form.setValue("share_with", note.share_with || []);
		setMultipleFiles(note.medias.map((media: any) => new File([media], media.name)));
		if (inputRef.current) {
			inputRef.current.value = note.to;
		}
	};

	const files = multipleFiles.map((file) => (
		<div
			key={file.name}
			className="cursor-pointer inline-flex flex-row items-center gap-1 p-1 mb-2 w-auto rounded-md whitespace-nowrap bg-blue-50 hover:bg-blue-100"
		>
			<span className="inline-flex text-blue-600">
				<IconFile size={14} stroke={1.5} />
			</span>
			<span className="text-sm font-semibold text-blue-600">{file.name}</span>
			<Button variant="link" size="xs" className="!p-0" onClick={closeModal}>
				<IconX size={14} stroke={2.5} />
			</Button>
		</div>
	));

	return (
		<Form {...form}>
			<form className="flex flex-col w-full gap-5" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-5">
					<div className="flex flex-row items-center justify-between w-full">
						<span className="text-sm font-semibold">Evidence Comments/Notes</span>
					</div>
					{notes.length !== 0 ? (
						<>
							{notes.map((note) => (
								<div key={note.id} className="flex flex-row gap-2">
									<div className="flex flex-row gap-2 w-full">
										<div className="flex items-start justify-start">
											<span className="flex h-[40px] w-[40px] rounded-full bg-primary-600 items-center justify-center text-white font-semibold shrink-0">
												{note.user.charAt(0).toUpperCase()}
											</span>
										</div>
										<div className="flex flex-col gap-2">
											<div className="flex flex-col">
												<span className="text-base font-semibold">{note.user}</span>
												<div className="inline-flex gap-1 items-center">
													<span className="text-sm text-foreground">To</span>
													<span className="text-sm font-semibold text-blue-600">
														{note.share_with?.length > 0 ? note.share_with.map((user: any) => user.label).join(", ") : "All"}
													</span>
													<span className="text-sm text-foreground">• {formatDate(note.updated_at, "PPp")}</span>
												</div>
											</div>
											<div className="text-base">{note.note}</div>
											<div>
												{note.medias.length !== 0 && (
													<span className="pr-1">
														{note.medias.map((file, index) => (
															<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={""} />
														))}
													</span>
												)}
											</div>
										</div>
									</div>
									{note.user_id === user?.id && (
										<DropdownMenu modal={false}>
											<DropdownMenuTrigger asChild>
												<Button className="!rounded-full shrink-0" type="button" size="icon" variant="ghost">
													<IconDots size={14} stroke={2} />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent>
												<DropdownMenuGroup>
													<DropdownMenuItem
														onClick={() => {
															handleEditNote(note);
														}}
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>Edit</span>
														</div>
													</DropdownMenuItem>

													<DropdownMenuItem
														onClick={() => {
															const modalId = modal.openModal({
																type: "confirm",
																variant: "red",
																title: "Delete Note",
																onConfirm: async () => {
																	try {
																		await caseEvidenceNoteDeleteMutation.mutateAsync(note.id);
																		modal.closeModal(modalId);
																	} catch (error) {}
																	modal.closeModal(modalId);
																},
															});
														}}
														dangerZone
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>Delete</span>
														</div>
													</DropdownMenuItem>
												</DropdownMenuGroup>
											</DropdownMenuContent>
										</DropdownMenu>
									)}
								</div>
							))}
						</>
					) : (
						<Blanket>
							<BlanketImage icon={<IconMoodLookDown size={30} stroke={1.5} />} />
							<BlanketInfo title="No Comments" message="No available comments for for now. Please check back later or refresh the page." />
						</Blanket>
					)}
				</div>

				<div className="flex flex-row gap-2 items-stretch">
					<div className="flex flex-row w-1/4 gap-1">
						<FormField
							control={form.control}
							name="share_with"
							render={({ field }) => (
								<FormItem>
									<FormGroup>
										<FormControl>
											<TagsInput
												isMulti={true}
												defaultValue={field.value}
												onValueChange={(value) => {
													field.onChange(value);
												}}
												placeholder={form.watch("share_with")?.length > 0 ? form.watch("share_with")[0].label : "To:"}
												className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground"
												loadOptions={async (value: string) => {
													const items = await userService.listByBranch(branchId);

													return items?.map((item) => ({
														label: `${item.full_name}`,
														value: item.id,
													}));
												}}
											/>
										</FormControl>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="flex flex-row items-center w-full gap-1">
						<div className="flex flex-col w-full border border-border rounded-md overflow-hidden px-3 h-full">
							<div className="flex flex-row items-center min-h-[38px]">
								<FormField
									control={form.control}
									name="note"
									render={({ field }) => (
										<FormItem>
											<FormGroup>
												<TextareaAutosize
													maxRows={5}
													className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground outline-none ring-0 focus:ring-0 resize-none"
													maxLength={1000}
													{...field}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
								<div className="inline-flex gap-2">
									<input
										type="file"
										accept="application/pdf, image/*"
										ref={inputRef}
										className="absolute inset-0 w-0 h-full cursor-pointer opacity-0"
										multiple
										onChange={handleFileChange}
										tabIndex={-1}
									/>
									<Tooltip className="z-[999]" content="Upload File" side="bottom">
										<button type="button" className="text-primary/60" onClick={() => inputRef.current?.click()}>
											<IconPaperclip size={18} stroke={1.5} />
										</button>
									</Tooltip>
									{form.watch("note") && (
										<button type="submit" disabled={form.formState.isSubmitting} className="text-primary-500">
											<IconSend2 size={18} stroke={1.5} />
										</button>
									)}
								</div>
							</div>
							<div className="flex flex-row gap-2">{files}</div>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
